import { AuthHelper } from "@/components/pages/Auth/AuthHelper";
import { UserLoginContext } from "@/components/pages/Auth/UserLoginProvider";
import { NumberFormatCustom } from "@/components/StoreComponents/NumberFormatCustom";
import { reloadProfile } from "@/redux/actions";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  formatPhone,
  getPhoneNumberMasking,
  validatePhone,
} from "@/utils/StringUtils";
import { Box, Button, styled, useTheme } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";

import { ButtonLoadingEllipsis } from "@/components/StoreComponents/StoreButton";
import OTPVerificationInput from "./OTPVerificationInput";

import { CheckoutButton } from "../CheckoutButton";
import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";
import { StyledLink } from "@/components/AppNavigation/StyledLink";
import { UNIVERSAL_STORE_SITE } from "@brandclub/common-ui";

const FormContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",

  width: "100%",
  ".title": {
    fontSize: "20px",
    fontWeight: 600,
    color: theme.palette.primary.main,
    textAlign: "center",
    marginBottom: "16px",
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
    },
  },
  ".description": {
    fontSize: "16px",
    fontWeight: 500,
    color: theme.palette.primary.main,
    textAlign: "center",
    marginBottom: "32px",
    [theme.breakpoints.down("md")]: {
      fontSize: "14px",
    },
  },
}));

const Terms = () => {
  const theme = useTheme();
  const domainConfig = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig
  );
  const linkPrefix =
    domainConfig?.storeBrandingType === "CustomDTCStore"
      ? UNIVERSAL_STORE_SITE
      : "";
  return (
    <Box
      display="flex"
      gap="8px"
      alignItems="flex-start"
      flexDirection="column"
      lineHeight="18px"
      fontWeight="500"
      sx={{
        width: "100%",
        maxWidth: "435px",
        textAlign: "center",
        display: "inline-block",
        fontSize: "14px",
        [theme.breakpoints.down("md")]: {
          fontSize: "12px",
        },
      }}
    >
      By continuing, you agree to Brandclub’s{" "}
      <StyledLink
        to={linkPrefix + "/terms"}
        target="_blank"
        rel="noopener noreferrer"
      >
        Terms
      </StyledLink>
      ,{" "}
      <StyledLink
        to={linkPrefix + "/privacy"}
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Policy
      </StyledLink>
      , and{" "}
      <StyledLink
        to={linkPrefix + "/smsterms"}
        target="_blank"
        rel="noopener noreferrer"
      >
        SMS Terms
      </StyledLink>
      . Standard messaging rates may apply.
    </Box>
  );
};

const EnterPhoneNumber = ({
  phoneNumber,
  setPhoneNumber,
  setOtpSession,
}: {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  setOtpSession: (otpSession: { username: string } | undefined) => void;
}) => {
  const theme = useTheme();
  const { signIn, submittingSignIn, clearOtpError } =
    useContext(UserLoginContext);

  const submitPhoneNumber = () => {
    clearOtpError();
    const phoneNumberInput = formatPhone(phoneNumber, "e164");
    signIn(phoneNumberInput, false, (_, session) => {
      setOtpSession(session);
    });
  };

  return (
    <FormContainer>
      <div className="title">Sign in or create an account</div>
      <div className="description">
        Enter your phone number to sign in or create an account.
      </div>

      <CheckoutInput
        label="Phone Number"
        name="phoneNumber"
        autoComplete="tel"
        value={phoneNumber}
        style={{
          minWidth: "300px",
          maxWidth: "380px",
          backgroundColor: "#fff",
        }}
        onChange={(e) => {
          const value = e.target.value;
          setPhoneNumber(value?.trim());
        }}
        autoFocus
        InputProps={{
          inputComponent: NumberFormatCustom as any,
          inputProps: {
            format: getPhoneNumberMasking(phoneNumber || ""),
          },
        }}
        sx={{
          flex: 1,
        }}
      />

      <CheckoutButton
        onClick={() => {
          submitPhoneNumber();
        }}
        sx={{
          maxWidth: "380px",
          width: "100%",
          height: "48px",

          marginTop: "24px",
          marginBottom: "32px",
          [theme.breakpoints.down("md")]: {
            height: "40px",
          },
        }}
        disabled={submittingSignIn || !validatePhone(phoneNumber)}
      >
        {submittingSignIn ? (
          <ButtonLoadingEllipsis
            baseFontSize={16}
            customColor={theme.palette.primary.main}
          />
        ) : (
          "Continue"
        )}
      </CheckoutButton>
      <Terms />
    </FormContainer>
  );
};

const Divider = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "1px",
        backgroundColor: "#dcdddc",
        margin: "31px 0",
      }}
    />
  );
};

const EnterOTP = ({
  phoneNumber,
  changePhoneNumber,
  otpSession,
  clearOtpSession,
  setOtpSession,
}: {
  phoneNumber: string;
  changePhoneNumber: () => void;
  otpSession: { username: string } | undefined;
  clearOtpSession: () => void;
  setOtpSession: (otpSession: { username: string } | undefined) => void;
}) => {
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [isSendingOTP, setIsSendingOTP] = useState<boolean>(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState<boolean>(false);
  const hasOtpSession = !!otpSession?.username;
  const lastSubmittedCodeRef = useRef<string>("");
  const dispatch = useAppDispatch();
  const {
    submittingSignIn,
    otpError,
    resendCoolDown,
    verifyOtp,
    resendOtp,
    clearOtpError,
  } = useContext(UserLoginContext);

  useEffect(() => {
    const OTP_EXPECTED_LENGTH = 6;
    if (
      verificationCode.length === OTP_EXPECTED_LENGTH &&
      verificationCode !== lastSubmittedCodeRef.current
    ) {
      console.log("verificationCode", verificationCode);
      lastSubmittedCodeRef.current = verificationCode;
      setIsVerifyingOTP(true);
      clearOtpError();
      verifyOtp(
        verificationCode,
        otpSession,
        () => {
          dispatch(reloadProfile());
          setIsVerifyingOTP(false);
        },
        () => {
          setIsVerifyingOTP(false);
        }
      );
    }
  }, [verifyOtp, verificationCode, otpSession, dispatch, clearOtpError]);

  useEffect(() => {
    // Check if the phone number entered is different from the one in the session; if so, clear the session
    if (
      hasOtpSession &&
      AuthHelper.getEmailForPhoneNumber(phoneNumber) !== otpSession.username
    ) {
      clearOtpSession();
    }
  }, [hasOtpSession, otpSession?.username, phoneNumber, clearOtpSession]);

  const resendOTP = () => {
    setIsSendingOTP(true);
    resendOtp(
      phoneNumber,
      false,
      (session) => {
        setOtpSession(session);
        setIsSendingOTP(false);
      },
      () => {
        setIsSendingOTP(false);
      }
    );
  };

  const theme = useTheme();
  return (
    <FormContainer>
      <div className="title">Verify your phone number</div>
      <div className="description">
        Please enter your verification code here.
      </div>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "380px",
        }}
      >
        <OTPVerificationInput
          value={verificationCode}
          onChange={(value) => {
            setVerificationCode(value);
            clearOtpError();
          }}
          maxLength={6}
          error={!!otpError}
          disabled={submittingSignIn || isSendingOTP || isVerifyingOTP}
        />

        <Divider />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          onClick={() => {
            if (resendCoolDown > 0 || submittingSignIn) {
              return;
            }
            setVerificationCode("");
            clearOtpError();
            resendOTP();
          }}
          variant="text"
          disableRipple
          sx={{
            textTransform: "none",
            color: theme.palette.primary.main,
            fontSize: "14px",
            fontWeight: 700,
            padding: "0",
            minWidth: "auto",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {resendCoolDown > 0
            ? `We resent your code. You can retry in ${resendCoolDown} seconds.`
            : "Send a new code"}
        </Button>
      </Box>
    </FormContainer>
  );
};

const SignInContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
  maxWidth: "656px",
  boxSizing: "border-box",
  backgroundColor: "#f6f8fa",
  padding: "32px 20px",
  borderRadius: "8px",
  transition: "all 0.3s ease",
  alignSelf: "center",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    maxWidth: "400px",
    padding: "20px 20px",
    border: "none",
  },
}));

const CheckoutSignIn = () => {
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const [otpSession, setOtpSession] =
    useState<{ username: string } | undefined>();

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <SignInContainer>
        {phoneNumber && otpSession ? (
          <EnterOTP
            phoneNumber={phoneNumber}
            changePhoneNumber={() => {
              setPhoneNumber("");
              setOtpSession(undefined);
            }}
            otpSession={otpSession}
            setOtpSession={setOtpSession}
            clearOtpSession={() => setOtpSession(undefined)}
          />
        ) : (
          <EnterPhoneNumber
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            setOtpSession={setOtpSession}
          />
        )}
      </SignInContainer>
      {phoneNumber && otpSession ? (
        <Box
          sx={(theme) => ({
            fontSize: "14px",
            marginTop: "32px",
            cursor: "pointer",
            color: theme.palette.primary.main,
            fontWeight: 500,
          })}
          onClick={() => {
            setPhoneNumber("");
            setOtpSession(undefined);
          }}
        >
          Back
        </Box>
      ) : null}
    </Box>
  );
};

export default CheckoutSignIn;
